import { Scene } from 'phaser';
import { DataManagerAsset } from '../systems/DataManagerAsset';

// Animation data interface
interface AnimationEntry {
  key: string;
  frames: string[];
  frameRate: number;
  repeat: number;
}

interface EntityAnimationData {
  texture: string;
  animations: AnimationEntry[];
}

interface AnimationsData {
  _meta: { extractedAt: string; source: string };
  entities: Record<string, EntityAnimationData>;
}

export class Preloader extends Scene {
  constructor() {
    super('Preloader');
  }

  init() {
    const { width, height } = this.scale;
    const centerX = width / 2;
    const centerY = height / 2;
    const storedLang = this.readStoredLanguage();
    const baseLabel = storedLang === 'es' ? 'Cargando...' : 'Loading...';

    // Fondo simple para que cargue menos "pesado"
    this.cameras.main.setBackgroundColor(0x0d1f33);

    // Loading text minimal
    const loadingText = this.add.text(centerX, centerY, baseLabel, {
      fontFamily: 'Arial',
      fontSize: '26px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Update percent (sin barra)
    this.load.on('progress', (progress: number) => {
      const percent = Math.round(progress * 100);
      const progressLabel = storedLang === 'es' ? `Cargando... ${percent}%` : `Loading... ${percent}%`;
      loadingText.setText(progressLabel);
    });
  }

  private readStoredLanguage(): 'es' | 'en' {
    try {
      const value = window?.localStorage?.getItem('imbasion_lang');
      if (value === 'en' || value === 'es') return value;
    } catch {
      // Nada, usa fallback
    }
    return 'es';
  }

  preload() {
    this.load.setPath('../assets');

    // Data files (required by DataManagerAsset and game systems)
    this.load.json('entities-c3data', 'data/entities-c3data.json');
    this.load.json('animations-c3data', 'data/animations-c3data.json');
    this.load.json('animations-overrides', 'data/animations-overrides.json');
    this.load.json('collision-c3data', 'data/collision-c3data.json');
    this.load.json('collision-overrides', 'data/collision-overrides.json');
    this.load.json('imagepoints-c3data', 'data/imagepoints-c3data.json');
    this.load.json('imagepoints-overrides', 'data/imagepoints-overrides.json');
    this.load.json('particles-c3data', 'data/particles-c3data.json');
    this.load.json('particles-overrides', 'data/particles-overrides.json');

    // Sound configuration files (data-driven audio system)
    this.load.json('sounds-c3data', 'data/sounds-c3data.json');
    this.load.json('entity-sounds-c3data', 'data/entity-sounds-c3data.json');
    this.load.json('sound-overrides', 'data/sound-overrides.json');

    // PowerUp effect definitions (data-driven powerup system)
    this.load.json('powerups-overrides', 'data/powerups-overrides.json');

    // Asset reference system (replaces GLOBAL_ATLAS)
    this.load.json('globals', 'data/globals.json');
    this.load.json('projectiles-c3data', 'data/projectiles-c3data.json');
    this.load.json('projectiles-overrides', 'data/projectiles-overrides.json');
    this.load.json('weapons-c3data', 'data/weapons-c3data.json');
    this.load.json('weapons-overrides', 'data/weapons-overrides.json');
    this.load.json('weapons-defaults', 'data/weapons-defaults.json');
    this.load.json('entities-overrides', 'data/entities-overrides.json');
    this.load.json('i18n-es', '../assets_v2/i18n/es.json');
    this.load.json('i18n-en', '../assets_v2/i18n/en.json');

    // Core assets
    this.load.image('logo', 'logo.png');

    // Menu assets (V2 rework)
    this.load.image('menu_v2_bg', '../assets_v2/menu_v2/Background_Menu.png');
    this.load.image('menu_v2_logo', '../assets_v2/menu_v2/New_Logo_Imbasion.png');
    this.load.image('menu_v2_cow', '../assets_v2/menu_v2/Cow.png');
    this.load.image('menu_v2_ufo', '../assets_v2/menu_v2/UFO.png');
    this.load.image('menu_v2_beam', '../assets_v2/menu_v2/Light_UFO.png');

    // Menu buttons (V2)
    this.load.image('menu_v2_btn_start_off', '../assets_v2/menu_v2/start1.png');
    this.load.image('menu_v2_btn_start_on', '../assets_v2/menu_v2/start2_on.png');
    this.load.image('menu_v2_btn_start_selected', '../assets_v2/menu_v2/start2.png');
    this.load.image('menu_v2_btn_start_pressed', '../assets_v2/menu_v2/start2_on.png');

    // Menu buttons (V2) - ES variants (si existen)
    this.load.image('menu_v2_btn_start_off_es', '../assets_v2/menu_v2/start1_es.png');
    this.load.image('menu_v2_btn_start_on_es', '../assets_v2/menu_v2/start2_on_es.png');
    this.load.image('menu_v2_btn_start_selected_es', '../assets_v2/menu_v2/start2_es.png');
    this.load.image('menu_v2_btn_start_pressed_es', '../assets_v2/menu_v2/start2_on_es.png');

    this.load.image('menu_v2_btn_continue_off', '../assets_v2/menu_v2/continue1.png');
    this.load.image('menu_v2_btn_continue_on', '../assets_v2/menu_v2/continue1_on.png');
    this.load.image('menu_v2_btn_continue_selected', '../assets_v2/menu_v2/continue2.png');
    this.load.image('menu_v2_btn_continue_pressed', '../assets_v2/menu_v2/continue2_on.png');

    this.load.image('menu_v2_btn_continue_off_es', '../assets_v2/menu_v2/continue1_es.png');
    this.load.image('menu_v2_btn_continue_on_es', '../assets_v2/menu_v2/continue1_on_es.png');
    this.load.image('menu_v2_btn_continue_selected_es', '../assets_v2/menu_v2/continue2_es.png');
    this.load.image('menu_v2_btn_continue_pressed_es', '../assets_v2/menu_v2/continue2_on_es.png');

    this.load.image('menu_v2_btn_exit_off', '../assets_v2/menu_v2/exit1.png');
    this.load.image('menu_v2_btn_exit_on', '../assets_v2/menu_v2/exit1_on.png');
    this.load.image('menu_v2_btn_exit_selected', '../assets_v2/menu_v2/exit2.png');
    this.load.image('menu_v2_btn_exit_pressed', '../assets_v2/menu_v2/exit2_on.png');

    this.load.image('menu_v2_btn_exit_off_es', '../assets_v2/menu_v2/exit1_es.png');
    this.load.image('menu_v2_btn_exit_on_es', '../assets_v2/menu_v2/exit1_on_es.png');
    this.load.image('menu_v2_btn_exit_selected_es', '../assets_v2/menu_v2/exit2_es.png');
    this.load.image('menu_v2_btn_exit_pressed_es', '../assets_v2/menu_v2/exit2_on_es.png');

    // Menu icons (V2)
    this.load.image('menu_v2_icon_settings_off', '../assets_v2/menu_v2/SETTINGS1.png');
    this.load.image('menu_v2_icon_settings_on', '../assets_v2/menu_v2/SETTINGS2.png');
    this.load.image('menu_v2_icon_sound_off', '../assets_v2/menu_v2/SOUND1.png');
    this.load.image('menu_v2_icon_sound_on', '../assets_v2/menu_v2/SOUND2.png');
    this.load.image('menu_v2_icon_social_off', '../assets_v2/menu_v2/SOCIAL1.png');
    this.load.image('menu_v2_icon_social_on', '../assets_v2/menu_v2/SOCIAL2.png');

    // UI Icons (game-icons.net + simpleicons.org)
    this.load.svg('icon_settings', 'ui/icon_settings.svg', { width: 64, height: 64 });
    this.load.svg('icon_sound', 'ui/icon_sound.svg', { width: 64, height: 64 });
    this.load.svg('icon_mute', 'ui/icon_mute.svg', { width: 64, height: 64 });
    this.load.svg('icon_facebook', 'ui/icon_facebook.svg', { width: 64, height: 64 });
    this.load.svg('icon_discord', 'ui/icon_discord.svg', { width: 64, height: 64 });
    this.load.svg('icon_web', 'ui/icon_web.svg', { width: 64, height: 64 });
    this.load.svg('icon_arrow_left', 'ui/icon_arrow_left.svg', { width: 64, height: 64 });

    // Level Select assets
    this.load.image('levelselect_bg', 'levelselect/menubackground.png');
    this.load.image('levelselect_title', 'levelselect/chooseyourlevel-default-000.png');
    // Level buttons (1-8)
    for (let i = 1; i <= 8; i++) {
      this.load.image(`btn_level${i}_off`, `levelselect/btnlevel${i}-off-000.png`);
      this.load.image(`btn_level${i}_on`, `levelselect/btnlevel${i}-on-000.png`);
      this.load.image(`btn_level${i}_selected`, `levelselect/btnlevel${i}-selected-000.png`);
    }
    // Back button
    this.load.image('btn_back_off', 'levelselect/backbutton-off-000.png');
    this.load.image('btn_back_on', 'levelselect/backbutton-on-000.png');
    this.load.image('btn_back_selected', 'levelselect/backbutton-selected-000.png');
    // Workshop button
    this.load.image('btn_workshop_off', 'levelselect/btnworkshop-off-000.png');
    this.load.image('btn_workshop_on', 'levelselect/btnworkshop-on-000.png');
    this.load.image('btn_workshop_selected', 'levelselect/btnworkshop-selected-000.png');
    // Lock icon for locked levels
    this.load.image('icon_lock', 'levelselect/lock-default-000.png');

    // ============================================
    // AUDIO - Music tracks
    // ============================================
    this.load.audio('music_intro', 'audio/music/intro.webm');
    this.load.audio('music_space_station', 'audio/music/space-station-247790.webm');
    this.load.audio('music_alien', 'audio/music/game-music-alien-71795.webm');
    this.load.audio('music_middle_age', 'audio/music/middle-age-254356.webm');
    this.load.audio('music_neon_gaming', 'audio/music/neon-gaming-128925.webm');
    this.load.audio('music_cave_temple', 'audio/music/cave-temple-fantasy-dark-20185.webm');
    this.load.audio('music_stranger_things', 'audio/music/stranger-things-124008.webm');
    this.load.audio('music_voyageur_galactic', 'audio/music/voyageur-galactic-01-114567.webm');
    this.load.audio('music_caverna2', 'audio/music/caverna2.webm');
    this.load.audio('pause_theme', 'audio/music/chill-pause-theme.webm');
    this.load.audio('music_game_over', 'audio/music/game_over.webm');

    // ============================================
    // AUDIO - UI SFX (using catalog keys)
    // ============================================
    this.load.audio('sfx_button_hover', 'audio/sfx/button1-VEED.webm');
    this.load.audio('sfx_button_click', 'audio/sfx/botton2.webm');
    // Also load with catalog keys for RuntimeManagerSoundEvent
    this.load.audio('sfx_button1', 'audio/sfx/button1.webm');
    this.load.audio('sfx_botton2', 'audio/sfx/botton2.webm');

    // ============================================
    // AUDIO - Projectile SFX (using catalog keys)
    // ============================================
    this.load.audio('sfx_lasershot', 'audio/sfx/laserShot.webm');
    this.load.audio('sfx_lasershotllarge', 'audio/sfx/laserShotLlarge.webm');
    this.load.audio('sfx_sonicray', 'audio/sfx/sonicRay.mp3');
    this.load.audio('sfx_missile_blast', 'audio/sfx/missile-blast.webm');
    this.load.audio('sfx_plasmabomb2', 'audio/sfx/plasmabomb2.webm');
    this.load.audio('sfx_hit_laser3', 'audio/sfx/hit-Laser3.webm');
    this.load.audio('sfx_hit_missile', 'audio/sfx/hit-missile.webm');
    this.load.audio('sfx_hit_explotion', 'audio/sfx/hit-explotion.webm');
    this.load.audio('sfx_hit_bala1', 'audio/sfx/hit-bala1.webm');
    this.load.audio('sfx_hit_bala2', 'audio/sfx/hit-bala2.webm');
    this.load.audio('sfx_hit_magmaball', 'audio/sfx/hit-MagmaBall.webm');
    this.load.audio('sfx_hit_missileboss', 'audio/sfx/hit-missileBoss.webm');
    this.load.audio('sfx_noammo', 'audio/sfx/noammo.webm');
    this.load.audio('sfx_damage_player', 'audio/sfx/damage_Player.webm');
    this.load.audio('sfx_mutashot2', 'audio/sfx/mutashot2.webm');
    this.load.audio('sfx_mutashot5', 'audio/sfx/mutashot5.webm');

    // ============================================
    // AUDIO - Tractor Beam SFX
    // ============================================
    this.load.audio('sfx_tractorsimple', 'audio/sfx/tractorSimple.webm');
    this.load.audio('sfx_tractormedium', 'audio/sfx/tractorMedium.webm');

    // ============================================
    // AUDIO - Boss SFX
    // ============================================
    this.load.audio('sfx_golemboss', 'audio/sfx/GolemBoss.webm');
    this.load.audio('sfx_big_laser_beam', 'audio/sfx/big-laser-beam.webm');
    this.load.audio('sfx_big_laser_beam2', 'audio/sfx/big-laser-beam2.webm');

    // ============================================
    // AUDIO - Explosion SFX (using catalog keys)
    // ============================================
    this.load.audio('sfx_explosionbasic', 'audio/sfx/explosionBasic.webm');
    this.load.audio('sfx_explosionmedium', 'audio/sfx/explosionMedium.webm');
    this.load.audio('sfx_explosionbig', 'audio/sfx/explosionBig.webm');
    this.load.audio('sfx_explosiontiled', 'audio/sfx/explosionTiled.webm');

    // ============================================
    // AUDIO - Destruction SFX (using catalog keys)
    // ============================================
    this.load.audio('sfx_escombrosalto', 'audio/sfx/escombrosAlto.webm');
    this.load.audio('sfx_escombrosmedio', 'audio/sfx/escombrosMedio.webm');
    this.load.audio('sfx_escombrosbajo', 'audio/sfx/escombrosBajo.webm');
    this.load.audio('sfx_glass_breaking', 'audio/sfx/glass-breaking.webm');
    this.load.audio('sfx_hitglasshight', 'audio/sfx/hitGlassHight.webm');
    this.load.audio('sfx_hitglassmedium', 'audio/sfx/hitGlassMedium.webm');
    this.load.audio('sfx_hitglasslow', 'audio/sfx/hitGlassLow.webm');

    // ============================================
    // AUDIO - Entity SFX (using catalog keys)
    // ============================================
    this.load.audio('sfx_cow_moo', 'audio/sfx/cow-moo.webm');
    this.load.audio('sfx_cow1', 'audio/sfx/cow1.webm');
    this.load.audio('sfx_boss1', 'audio/sfx/boss1.webm');
    this.load.audio('sfx_defaultadbdutable', 'audio/sfx/defaultAdbdutable.webm');
    this.load.audio('sfx_items2', 'audio/sfx/items2.webm');

    // ============================================
    // GLOBAL GAME ASSETS (always loaded)
    // Per-level assets are loaded by LevelPreloader
    // ============================================

    // Player explosion (player atlas is loaded via GLOBAL_ATLAS)
    this.load.image('playerexplotion', 'game/player/playerexplotion.png');

    // HUD Icons
    this.load.image('hud_heart', 'game/ui/heart-icon.png');
    this.load.image('hud_biomoney', 'game/ui/biomoney-icon.png');

    // Abduction effects
    this.load.image('biomoney_particle', 'game/effects/biomoney_particle.png');
    this.load.audio('sfx_biomoney', 'audio/sfx/biomoney.webm');

    // Particle system images (from C3 extraction)
    this.load.image('particles5', 'game/effects/particles5.png');
    this.load.image('particlehay', 'game/effects/particlehay.png');
    this.load.image('particlesmokereactor', 'game/effects/particlesmokereactor.png');
    this.load.image('particlegreenleak', 'game/effects/particlegreenleak.png');
    this.load.image('adornobiomoney', 'game/effects/adornobiomoney.png');
    this.load.image('laserlongparticle', 'game/effects/laserlongparticle.png');
    this.load.image('ex9000leak', 'game/effects/ex9000leak.png');
    this.load.image('musicalnote', 'game/effects/musicalnote.png');

    // Environment/Structure images
    this.load.image('sonic_crystal', 'game/environment/sonic_crystal.png');

    // Sonic ring for burst projectile
    this.load.image('SonicRing', 'game/projectiles/SonicRing.png');

    // Note: Global atlas assets are now loaded dynamically via DataManagerAsset in create()

    // Tractor Beam frames (puny)
    for (let i = 0; i <= 8; i++) {
      const frame = i.toString().padStart(3, '0');
      this.load.image(`punytractorbeam_start_${i}`, `game/weapons/tractorbeam/punytractorbeam-start-${frame}.png`);
      this.load.image(`punytractorbeam_fallback_${i}`, `game/weapons/tractorbeam/punytractorbeam-fallback-${frame}.png`);
    }
    this.load.image('punytractorbeam_hold_0', 'game/weapons/tractorbeam/punytractorbeam-hold-000.png');

    // Tractor Beam frames (mega)
    for (let i = 0; i <= 10; i++) {
      const frame = i.toString().padStart(3, '0');
      this.load.image(`megatractorbeam_start_${i}`, `game/weapons/tractorbeam/megatractorbeam-start-${frame}.png`);
      this.load.image(`megatractorbeam_fallback_${i}`, `game/weapons/tractorbeam/megatractorbeam-fallback-${frame}.png`);
    }
    this.load.image('megatractorbeam_hold_0', 'game/weapons/tractorbeam/megatractorbeam-hold-000.png');
  }

  create() {
    // Create placeholder texture for missing assets
    this.createPlaceholderTexture();

    // Create tractor beam animations (still manual - frame-based)
    this.createTractorBeamAnimations();

    // Initialize DataManagerAsset and resolve global assets
    DataManagerAsset.load(this);
    const globalAssets = DataManagerAsset.resolveGlobals();

    // Filter out already loaded textures
    const atlasToLoad = [...globalAssets.atlas].filter(key => !this.textures.exists(key));

    if (atlasToLoad.length > 0) {
      console.log(`[Preloader] Loading ${atlasToLoad.length} global atlas textures via DataManagerAsset`);

      // Queue atlas assets for loading
      this.load.setPath('../assets');
      for (const key of atlasToLoad) {
        this.load.atlas(
          key,
          `game/spritesheets/${key}.png`,
          `game/spritesheets/${key}.json`
        );
      }

      // Handle load completion
      this.load.once('complete', () => {
        // Create animations for loaded assets
        this.createGlobalAnimations(atlasToLoad);
        this.proceedToMainMenu();
      });

      // Start loading
      this.load.start();
    } else {
      // No assets to load, proceed directly
      this.proceedToMainMenu();
    }
  }

  /**
   * Proceed to MainMenu scene
   */
  private proceedToMainMenu(): void {
    this.scene.start('MainMenu');
  }

  /**
   * Create a placeholder texture for missing assets (pink box with X)
   */
  private createPlaceholderTexture(): void {
    if (this.textures.exists('__missing__')) return;

    const size = 32;
    const graphics = this.make.graphics({ x: 0, y: 0 });

    // Pink/magenta background (classic missing texture color)
    graphics.fillStyle(0xff00ff, 1);
    graphics.fillRect(0, 0, size, size);

    // Black border
    graphics.lineStyle(2, 0x000000, 1);
    graphics.strokeRect(1, 1, size - 2, size - 2);

    // Black X
    graphics.lineStyle(2, 0x000000, 1);
    graphics.lineBetween(4, 4, size - 4, size - 4);
    graphics.lineBetween(size - 4, 4, 4, size - 4);

    // Generate texture
    graphics.generateTexture('__missing__', size, size);
    graphics.destroy();
  }

  /**
   * Create animations from animations-c3data.json for loaded atlas entities
   * @param atlasKeys List of atlas keys that were loaded
   */
  private createGlobalAnimations(atlasKeys: string[]): void {
    const animData = this.cache.json.get('animations-c3data') as AnimationsData | undefined;
    const overrides = this.cache.json.get('animations-overrides') as AnimationsData | undefined;

    if (!animData) {
      return;
    }

    // Merge overrides into animData
    if (overrides?.entities) {
      for (const [entityName, entityOverride] of Object.entries(overrides.entities)) {
        if (!animData.entities[entityName]) {
          // Nueva entidad del override
          animData.entities[entityName] = entityOverride;
        } else {
          // Merge animaciones: override reemplaza animaciones con mismo key
          for (const overrideAnim of entityOverride.animations) {
            const existingIndex = animData.entities[entityName].animations
              .findIndex(a => a.key === overrideAnim.key);
            if (existingIndex >= 0) {
              // Reemplazar animación existente
              animData.entities[entityName].animations[existingIndex] = overrideAnim;
            } else {
              // Agregar nueva animación
              animData.entities[entityName].animations.push(overrideAnim);
            }
          }
        }
      }
    }

    for (const entity of atlasKeys) {
      const data = animData.entities[entity];
      if (!data) {
        continue;
      }

      // Verify texture exists
      if (!this.textures.exists(data.texture)) {
        continue;
      }

      for (const anim of data.animations) {
        // Skip if animation already exists
        if (this.anims.exists(anim.key)) {
          continue;
        }

        // Build frames array
        const frames = anim.frames.map(f => ({ key: data.texture, frame: f }));

        this.anims.create({
          key: anim.key,
          frames: frames,
          frameRate: anim.frameRate,
          repeat: anim.repeat,
        });
      }
    }
  }

  /**
   * Create tractor beam animations from loaded frames
   */
  private createTractorBeamAnimations(): void {
    // Puny Tractor Beam animations
    this.anims.create({
      key: 'punytractorbeam_start',
      frames: Array.from({ length: 9 }, (_, i) => ({ key: `punytractorbeam_start_${i}` })),
      frameRate: 20,
      repeat: 0,
    });

    this.anims.create({
      key: 'punytractorbeam_hold',
      frames: [{ key: 'punytractorbeam_hold_0' }],
      frameRate: 5,
      repeat: -1, // Loop while holding
    });

    this.anims.create({
      key: 'punytractorbeam_fallback',
      frames: Array.from({ length: 9 }, (_, i) => ({ key: `punytractorbeam_fallback_${i}` })),
      frameRate: 20,
      repeat: 0,
    });

    // Mega Tractor Beam animations
    this.anims.create({
      key: 'megatractorbeam_start',
      frames: Array.from({ length: 11 }, (_, i) => ({ key: `megatractorbeam_start_${i}` })),
      frameRate: 20,
      repeat: 0,
    });

    this.anims.create({
      key: 'megatractorbeam_hold',
      frames: [{ key: 'megatractorbeam_hold_0' }],
      frameRate: 10,
      repeat: -1,
    });

    this.anims.create({
      key: 'megatractorbeam_fallback',
      frames: Array.from({ length: 11 }, (_, i) => ({ key: `megatractorbeam_fallback_${i}` })),
      frameRate: 20,
      repeat: 0,
    });
  }
}
